/* eslint-disable react/destructuring-assignment */
import { Icon, IconButton } from '@chakra-ui/react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import {
    CreatableSelect as BaseSelect,
    ClearIndicatorProps,
    DropdownIndicatorProps,
    GroupBase,
    CreatableProps,
    chakraComponents,
} from 'chakra-react-select';
import { FaIcon } from '../fa-icon';

export interface CreatableSelectOption<T> {
    label: string;
    value: T;
}

interface SelectProps {
    placeholderColor?: string;
    optionColorScheme?: string;
}

const components = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ClearIndicator: (props: ClearIndicatorProps<any, boolean, any>) => (
        <chakraComponents.ClearIndicator {...props}>
            <IconButton
                colorScheme="blackAlpha"
                size="xs"
                pointerEvents="all"
                variant="ghost"
                aria-label="clear selection"
                icon={<FaIcon icon={faTimes} />}
            />
        </chakraComponents.ClearIndicator>
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DropdownIndicator: (props: DropdownIndicatorProps<any, boolean, any>) => (
        <chakraComponents.DropdownIndicator {...props}>
            <Icon fontSize={props.selectProps.size}>
                <path fill="currentColor" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
            </Icon>
        </chakraComponents.DropdownIndicator>
    ),
};

export function CreatableSelect<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>({
    placeholderColor = 'gray.500',
    optionColorScheme = 'gray',
    colorScheme,
    ...selectProps
}: CreatableProps<Option, IsMulti, Group> & SelectProps) {
    return (
        <BaseSelect
            {...selectProps}
            useBasicStyles
            colorScheme={colorScheme}
            chakraStyles={{
                menu: (provided) => ({
                    ...provided,
                    zIndex: 3,
                    boxShadow: 'sm',
                    borderRadius: 'md',
                    color: 'initial',
                    fontWeight: 'initial',
                }),
                option: (provided, state) => ({
                    ...provided,
                    _selected: {
                        bgColor: `${optionColorScheme}.300`,
                    },
                    _hover: {
                        bgColor: !state.isSelected ? `${optionColorScheme}.100` : undefined,
                    },
                }),
                menuList: (provided) => ({
                    ...provided,
                    borderRadius: 'md',
                    boxShadow: 'unset',
                }),
                multiValue: (provided) => ({
                    ...provided,
                    bgColor: 'gray.100',
                    color: 'black',
                }),
                clearIndicator: (provided) => ({
                    ...provided,
                    mr: 0,
                    _hover: {
                        bgColor: 'transparent',
                    },
                }),
                dropdownIndicator: (provided) => ({
                    ...provided,
                    ml: 0,
                    width: 'unset',
                    mr: 3,
                    paddingInlineEnd: 0,
                    color: 'black',
                }),
                placeholder: (provided) => ({
                    ...provided,
                    color: placeholderColor,
                }),
                loadingIndicator: (provided) => ({
                    ...provided,
                    color: 'gray.300',
                }),
                container: (provided) => ({
                    ...provided,
                    width: 'full',
                }),
            }}
            components={components}
        />
    );
}
